import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ProfessionalLayout from '../../components/layout/ProfessionalLayout';
import { employeeAPI } from '../../services/api';
import { Loader2, ArrowLeft, Flag, AlertTriangle, Share2, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import './RecordDetailPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecordDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeField, setDisputeField] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [creatingShareLink, setCreatingShareLink] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const fetchRecord = async () => {
    try {
      const response = await employeeAPI.getRecord(id);
      setRecord(response.data);
    } catch (error) {
      console.error('Error fetching record:', error);
      toast.error('Failed to load record');
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    const s = (status || 'draft').toLowerCase();
    if (s === 'published' || s === 'verified') return 'pro-status-published';
    if (s === 'invited' || s === 'pending' || s === 'awaiting' || s === 'acknowledged') return 'pro-status-invited';
    if (s === 'disputed') return 'pro-status-disputed';
    return 'pro-status-draft';
  };

  const getStatusLabel = (status) => {
    const s = (status || 'draft').toLowerCase();
    if (s === 'published' || s === 'verified') return 'Published';
    if (s === 'acknowledged') return 'Acknowledged';
    if (s === 'invited' || s === 'pending' || s === 'awaiting') return 'Awaiting Employer';
    if (s === 'disputed') return 'Disputed';
    return 'Draft';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="detail-no-rating">Not yet rated</span>;
    return (
      <span className="detail-stars">
        {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
      </span>
    );
  };

  const getLifecycleStage = (status) => {
    const s = (status || 'draft').toLowerCase();
    if (s === 'published' || s === 'verified') return 'published';
    if (s === 'acknowledged') return 'acknowledged';
    if (s === 'invited' || s === 'pending' || s === 'awaiting') return 'invited';
    if (s === 'disputed') return 'disputed';
    return 'draft';
  };

  const handleDispute = async () => {
    if (!disputeField.trim() || !disputeReason.trim()) {
      toast.error('Please select a field and provide a reason for the dispute');
      return;
    }
    
    setSubmittingDispute(true);
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/disputes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          record_id: id,
          field: disputeField,
          account_text: disputeReason
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit dispute');
      }
      
      toast.success('Dispute submitted successfully');
      setShowDisputeModal(false);
      setDisputeField('');
      setDisputeReason('');
      fetchRecord(); // Refresh
    } catch (error) {
      toast.error(error.message || 'Failed to submit dispute');
    } finally {
      setSubmittingDispute(false);
    }
  };

  const createShareLink = async () => {
    setCreatingShareLink(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/sharing/records/create-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          record_id: id,
          expires_in_days: 30
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create share link');
      }
      
      const data = await response.json();
      setShareLink({
        token: data.share_token,
        url: `${API_URL}${data.share_url}`,
        expires_at: data.expires_at
      });
      setShowShareModal(true);
      toast.success('Share link created!');
    } catch (error) {
      toast.error(error.message || 'Failed to create share link');
    } finally {
      setCreatingShareLink(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink.url);
      setCopiedShare(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopiedShare(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const isShareable = ['acknowledged', 'published', 'verified'].includes(record?.status?.toLowerCase());

  // Generate lifecycle events from record data
  const getLifecycleEvents = () => {
    const events = [];
    
    if (record?.created_at) {
      events.push({
        icon: '👤',
        title: `${record.employee_name || 'You'} requested this record`,
        date: formatDateTime(record.created_at)
      });
    }
    
    if (record?.invited_at || record?.status === 'invited') {
      events.push({
        icon: '📧',
        title: `Verification request sent to ${record.employer_email || 'employer'}`,
        date: formatDateTime(record.invited_at || record.created_at)
      });
    }
    
    if (record?.employer_response_at) {
      events.push({
        icon: '💼',
        title: `${record.employer_name || 'Employer'} completed the assessment`,
        date: formatDateTime(record.employer_response_at)
      });
    }
    
    if (record?.manager_rating_at) {
      events.push({
        icon: '✏️',
        title: 'Manager submitted the performance rating',
        date: formatDateTime(record.manager_rating_at)
      });
    }
    
    if (record?.acknowledged_at) {
      events.push({
        icon: '✓',
        title: `${record.employee_name || 'You'} reviewed and acknowledged the record`,
        date: formatDateTime(record.acknowledged_at)
      });
    }
    
    if (record?.published_at || record?.status === 'published') {
      events.push({
        icon: '🔒',
        title: 'Record locked and published to your VETT ID',
        date: formatDateTime(record.published_at || record.updated_at)
      });
    }
    
    if (record?.disputed_at || record?.status === 'disputed') {
      events.push({
        icon: '⚠️',
        title: 'Record disputed - under review',
        date: formatDateTime(record.disputed_at)
      });
    }
    
    return events.length > 0 ? events : [
      { icon: '👤', title: 'Record created', date: formatDateTime(record?.created_at || new Date()) }
    ];
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F8EF7]" />
        </div>
      </ProfessionalLayout>
    );
  }

  if (!record) {
    return (
      <ProfessionalLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-bold text-[var(--pro-light-text)]">Record not found</h2>
          <Link to="/professional/records" className="pro-btn pro-btn-primary mt-4">
            Back to records
          </Link>
        </div>
      </ProfessionalLayout>
    );
  }

  const currentStage = getLifecycleStage(record.status);
  const lifecycleEvents = getLifecycleEvents();

  return (
    <ProfessionalLayout>
      {/* Header */}
      <div className="detail-top-row">
        <div>
          <Link to="/professional/records" className="detail-back-link">
            <ArrowLeft className="w-4 h-4" />
            BACK TO RECORDS
          </Link>
          <h1 className="pro-page-title">
            {record.company_name || record.employer_name || 'Company'}, {record.job_title || record.role || 'Role'}
          </h1>
          <p className="pro-page-sub">
            {formatDate(record.start_date)}{record.is_current ? ', current' : record.end_date ? `, ${formatDate(record.end_date)}` : ''}. 
            Record ID <span className="mono">{record.record_id || `REC-${record.id?.slice(0, 6).toUpperCase()}`}</span>
          </p>
        </div>
        <div className="detail-header-actions">
          {isShareable && (
            <button 
              className="share-record-btn"
              onClick={createShareLink}
              disabled={creatingShareLink}
              data-testid="share-record-btn"
            >
              {creatingShareLink ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Share2 className="w-4 h-4" />
              )}
              Share
            </button>
          )}
          <span className={`pro-status-badge ${getStatusClass(record.status)}`} style={{ fontSize: '13px', padding: '8px 16px' }}>
            {getStatusLabel(record.status)}
          </span>
        </div>
      </div>

      {/* Lifecycle Card */}
      <div className="pro-card" style={{ marginBottom: '24px' }}>
        <div className="pro-card-title" style={{ marginBottom: '20px' }}>Lifecycle</div>
        <div className="lifecycle-timeline" data-stage={currentStage} data-testid="lifecycle-timeline">
          <div className={`timeline-node ${currentStage === 'draft' || ['invited', 'acknowledged', 'published'].includes(currentStage) ? 'completed' : ''} ${currentStage === 'draft' ? 'current' : ''}`}>
            <div className="timeline-node-line" />
            <div className="timeline-node-dot">1</div>
            <div className="timeline-node-label">Draft</div>
          </div>
          <div className={`timeline-node ${['invited', 'acknowledged', 'published'].includes(currentStage) ? 'completed' : ''} ${currentStage === 'invited' ? 'current' : ''}`}>
            <div className="timeline-node-line" />
            <div className="timeline-node-dot">2</div>
            <div className="timeline-node-label">Invited</div>
          </div>
          <div className={`timeline-node ${['acknowledged', 'published'].includes(currentStage) ? 'completed' : ''} ${currentStage === 'acknowledged' ? 'current' : ''}`}>
            <div className="timeline-node-line" />
            <div className="timeline-node-dot">3</div>
            <div className="timeline-node-label">Acknowledged</div>
          </div>
          <div className={`timeline-node ${currentStage === 'published' ? 'completed current' : ''}`}>
            <div className="timeline-node-line" />
            <div className="timeline-node-dot">4</div>
            <div className="timeline-node-label">Published</div>
          </div>
          <div className={`timeline-node disputed ${currentStage === 'disputed' ? 'active' : ''}`}>
            <div className="timeline-node-line" />
            <div className="timeline-node-dot">!</div>
            <div className="timeline-node-label">Disputed</div>
          </div>
        </div>
        <p className="detail-hint">
          A record can branch to Disputed if either party flags an inaccuracy before archiving, 
          and resolves back to Published or moves to Archived. 
          {record.status !== 'disputed' ? ' This record has not been disputed.' : ''}
        </p>
      </div>

      {/* Who Confirmed What */}
      <div className="pro-card" style={{ marginBottom: '24px' }}>
        <div className="pro-card-title">Who confirmed what</div>
        <div className="timeline-detail-list" data-testid="timeline-events">
          {lifecycleEvents.map((event, index) => (
            <div key={index} className="timeline-detail-item">
              <div className="timeline-detail-icon">{event.icon}</div>
              <div className="timeline-detail-body">
                <div className="timeline-detail-title">{event.title}</div>
                <div className="timeline-detail-meta">{event.date}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Record Detail */}
      <div className="pro-card">
        <div className="pro-card-title">Record detail</div>
        <div className="detail-meta-grid">
          <div className="detail-meta-item">
            <div className="detail-meta-label">Company</div>
            <div className="detail-meta-val">{record.company_name || record.employer_name || '-'}</div>
          </div>
          <div className="detail-meta-item">
            <div className="detail-meta-label">Role</div>
            <div className="detail-meta-val">{record.job_title || record.role || '-'}</div>
          </div>
          <div className="detail-meta-item">
            <div className="detail-meta-label">Dates</div>
            <div className="detail-meta-val">
              {formatDate(record.start_date)}{record.is_current ? ', current' : record.end_date ? `, ${formatDate(record.end_date)}` : ''}
            </div>
          </div>
          <div className="detail-meta-item">
            <div className="detail-meta-label">Departure reason</div>
            <div className="detail-meta-val">{record.is_current ? 'Currently employed' : (record.departure_reason || 'Not specified')}</div>
          </div>
          <div className="detail-meta-item">
            <div className="detail-meta-label">Employment type</div>
            <div className="detail-meta-val">{record.employment_type || 'Full-time'}</div>
          </div>
          <div className="detail-meta-item">
            <div className="detail-meta-label">Performance rating</div>
            <div>{renderStars(record.performance_rating)}</div>
          </div>
        </div>

        {record.closing_statement && (
          <>
            <hr className="detail-divider" />
            <div className="detail-meta-label" style={{ marginBottom: '10px' }}>Manager's closing statement</div>
            <div className="detail-closing-statement">"{record.closing_statement}"</div>
          </>
        )}

        {/* Actions */}
        <div className="detail-actions">
          {['acknowledged', 'published', 'verified'].includes((record.status || '').toLowerCase()) && (
            <button 
              className="pro-btn pro-btn-ghost"
              onClick={() => setShowDisputeModal(true)}
              data-testid="dispute-btn"
            >
              <Flag className="w-4 h-4" />
              Dispute this record
            </button>
          )}
        </div>
      </div>

      {/* Dispute Modal */}
      {showDisputeModal && (
        <div className="dispute-modal-overlay" onClick={() => setShowDisputeModal(false)}>
          <div className="dispute-modal" onClick={e => e.stopPropagation()} data-testid="dispute-modal">
            <div className="dispute-modal-header">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <h3>Dispute this record</h3>
            </div>
            <p className="dispute-modal-desc">
              Select the field you want to dispute and provide details about the inaccuracy. 
              The employer will be notified and asked to respond.
            </p>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--pro-light-text)', marginBottom: '8px' }}>
                Field to dispute
              </label>
              <select
                value={disputeField}
                onChange={(e) => setDisputeField(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '14px',
                  background: 'var(--pro-light-bg)',
                  border: '1px solid var(--pro-light-border)',
                  borderRadius: '10px',
                  color: 'var(--pro-light-text)'
                }}
                data-testid="dispute-field-select"
              >
                <option value="">Select a field...</option>
                <option value="job_title">Job Title / Role</option>
                <option value="dates">Employment Dates</option>
                <option value="performance_rating">Performance Rating</option>
                <option value="employment_type">Employment Type</option>
                <option value="closing_statement">Closing Statement</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              placeholder="Describe what is inaccurate about this field..."
              className="dispute-textarea"
              rows={4}
              data-testid="dispute-reason-input"
            />
            <div className="dispute-modal-actions">
              <button 
                className="pro-btn pro-btn-ghost"
                onClick={() => {
                  setShowDisputeModal(false);
                  setDisputeField('');
                  setDisputeReason('');
                }}
              >
                Cancel
              </button>
              <button 
                className="pro-btn"
                style={{ background: '#EF4444', color: '#fff' }}
                onClick={handleDispute}
                disabled={submittingDispute || !disputeField}
                data-testid="submit-dispute-btn"
              >
                {submittingDispute ? 'Submitting...' : 'Submit Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareLink && (
        <div className="dispute-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="dispute-modal share-modal" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            <div className="dispute-modal-header">
              <h3>Share this record</h3>
              <button className="dispute-modal-close" onClick={() => setShowShareModal(false)}>×</button>
            </div>
            <p style={{ color: 'var(--pro-light-text-muted)', marginBottom: '20px', fontSize: '14px' }}>
              Anyone with this link can view this verified record. The link expires in 30 days.
            </p>
            
            <div className="share-link-box">
              <input 
                type="text" 
                value={shareLink.url} 
                readOnly 
                className="share-link-input mono"
                data-testid="share-link-input"
              />
              <button 
                className="share-link-copy"
                onClick={copyShareLink}
              >
                {copiedShare ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <a 
                href={shareLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="share-link-open"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            <div className="share-info-row">
              <span>Share Token:</span>
              <span className="mono">{shareLink.token}</span>
            </div>
            <div className="share-info-row">
              <span>Expires:</span>
              <span>{new Date(shareLink.expires_at).toLocaleDateString()}</span>
            </div>
            
            <div className="dispute-modal-actions" style={{ marginTop: '24px' }}>
              <button 
                className="pro-btn pro-btn-primary"
                onClick={copyShareLink}
              >
                {copiedShare ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ProfessionalLayout>
  );
};

export default RecordDetailPage;
